import { useRef, useCallback } from 'react';
import { Textarea } from './ui/textarea';
import { Bold, Italic, List, ListOrdered, Link as LinkIcon, Heading } from 'lucide-react';

/**
 * Lightweight markdown editor — toolbar that inserts syntax around the selection
 * in a plain <Textarea>. Output is still plain text (markdown), so storage, search,
 * and legacy descriptions all keep working. Use <MarkdownView text={...} /> to render.
 *
 * Supported syntax: **bold**, *italic*, __underline__, - bullet, 1. ordered,
 * [text](url), ## heading, newline → <br>.
 */
export default function RichTextEditor({ value, onChange, placeholder, minHeight = 120, className = '' }) {
  const ref = useRef(null);

  const wrap = useCallback((before, after = before) => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = ta.value;
    const selected = text.slice(start, end) || 'text';
    const next = text.slice(0, start) + before + selected + after + text.slice(end);
    onChange(next);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + before.length, start + before.length + selected.length);
    }, 0);
  }, [onChange]);

  const lineAction = useCallback((prefix) => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = ta.value;
    // expand to whole lines
    const lineStart = text.lastIndexOf('\n', start - 1) + 1;
    const lineEnd = text.indexOf('\n', end);
    const lastIdx = lineEnd === -1 ? text.length : lineEnd;
    const block = text.slice(lineStart, lastIdx);
    const lines = block.split('\n');
    const allPrefixed = lines.every((l) => l.startsWith(prefix));
    const next = lines.map((l, i) => {
      if (allPrefixed) return l.slice(prefix.length);
      if (typeof prefix === 'string' && prefix.startsWith('1.')) return `${i + 1}. ${l.replace(/^\d+\.\s*/, '')}`;
      return prefix + l;
    }).join('\n');
    const nextText = text.slice(0, lineStart) + next + text.slice(lastIdx);
    onChange(nextText);
    setTimeout(() => ta.focus(), 0);
  }, [onChange]);

  const insertLink = useCallback(() => {
    const url = window.prompt('Link URL');
    if (!url) return;
    wrap('[', `](${url})`);
  }, [wrap]);

  return (
    <div className={`rounded-xl border border-slate-200 bg-white ${className}`}>
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-100">
        <ToolbarBtn onClick={() => wrap('**')} title="Bold"><Bold className="w-3.5 h-3.5" /></ToolbarBtn>
        <ToolbarBtn onClick={() => wrap('*')} title="Italic"><Italic className="w-3.5 h-3.5" /></ToolbarBtn>
        <ToolbarBtn onClick={() => wrap('__')} title="Underline"><span className="text-xs font-semibold underline">U</span></ToolbarBtn>
        <span className="w-px h-4 bg-slate-200 mx-0.5" />
        <ToolbarBtn onClick={() => lineAction('## ')} title="Heading"><Heading className="w-3.5 h-3.5" /></ToolbarBtn>
        <ToolbarBtn onClick={() => lineAction('- ')} title="Bullet list"><List className="w-3.5 h-3.5" /></ToolbarBtn>
        <ToolbarBtn onClick={() => lineAction('1. ')} title="Numbered list"><ListOrdered className="w-3.5 h-3.5" /></ToolbarBtn>
        <ToolbarBtn onClick={insertLink} title="Link"><LinkIcon className="w-3.5 h-3.5" /></ToolbarBtn>
        <span className="ml-auto text-[10px] text-slate-400 pr-1">Markdown</span>
      </div>
      <Textarea
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="border-0 rounded-t-none rounded-b-xl focus-visible:ring-0 focus-visible:ring-offset-0"
        style={{ minHeight }}
      />
    </div>
  );
}

function ToolbarBtn({ onClick, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center text-slate-gray hover:text-dark-slate"
    >
      {children}
    </button>
  );
}

/**
 * Render markdown as React nodes. No new deps.
 * Supports inline: **bold**, *italic*, __underline__, [text](url), `code`.
 * Block: ## heading, - bullet, 1. numbered, blank line = paragraph break.
 * URLs in link syntax are restricted to http(s)/mailto to prevent javascript: injection.
 */
export function MarkdownView({ text, className = '' }) {
  if (!text) return null;
  const str = typeof text === 'string' ? text : String(text);
  // Normalize line endings
  const lines = str.replace(/\r\n/g, '\n').split('\n');

  // Group into blocks: ul, ol, heading, paragraph, blank
  const blocks = [];
  let cur = null;
  const flush = () => { if (cur) { blocks.push(cur); cur = null; } };
  for (const line of lines) {
    if (line.trim() === '') { flush(); continue; }
    if (line.startsWith('## ')) { flush(); blocks.push({ type: 'h', content: line.slice(3) }); continue; }
    if (/^[-*]\s+/.test(line)) {
      if (!cur || cur.type !== 'ul') { flush(); cur = { type: 'ul', items: [] }; }
      cur.items.push(line.replace(/^[-*]\s+/, ''));
      continue;
    }
    if (/^\d+\.\s+/.test(line)) {
      if (!cur || cur.type !== 'ol') { flush(); cur = { type: 'ol', items: [] }; }
      cur.items.push(line.replace(/^\d+\.\s+/, ''));
      continue;
    }
    // paragraph — join consecutive non-blank lines
    if (!cur || cur.type !== 'p') { flush(); cur = { type: 'p', content: line }; }
    else cur.content += '\n' + line;
  }
  flush();

  return (
    <div className={`markdown-view ${className}`}>
      {blocks.map((b, i) => {
        if (b.type === 'h') return <h3 key={i} className="font-heading font-bold text-dark-slate text-base mt-3 mb-1">{renderInline(b.content)}</h3>;
        if (b.type === 'ul') return (
          <ul key={i} className="list-disc list-inside space-y-1 my-2 text-slate-gray">
            {b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
          </ul>
        );
        if (b.type === 'ol') return (
          <ol key={i} className="list-decimal list-inside space-y-1 my-2 text-slate-gray">
            {b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
          </ol>
        );
        return (
          <p key={i} className="text-slate-gray whitespace-pre-wrap my-2">{renderInline(b.content)}</p>
        );
      })}
    </div>
  );
}

// Inline token parser — handles **bold**, *italic*, __underline__, `code`, [text](url)
function renderInline(text) {
  const out = [];
  let i = 0;
  let key = 0;
  const pushText = (s) => { if (s) out.push(s); };

  while (i < text.length) {
    const rest = text.slice(i);
    // Link [text](url)
    const linkM = rest.match(/^\[([^\]]+)\]\(([^)]+)\)/);
    if (linkM) {
      const url = linkM[2].trim();
      if (/^(https?:|mailto:)/i.test(url)) {
        out.push(<a key={`k${key++}`} href={url} target="_blank" rel="noopener noreferrer" className="text-mint-fresh underline">{renderInline(linkM[1])}</a>);
      } else {
        out.push(linkM[1]);
      }
      i += linkM[0].length; continue;
    }
    // Bold **text**
    const boldM = rest.match(/^\*\*([^*]+)\*\*/);
    if (boldM) { out.push(<strong key={`k${key++}`} className="font-semibold text-dark-slate">{renderInline(boldM[1])}</strong>); i += boldM[0].length; continue; }
    // Underline __text__
    const undM = rest.match(/^__([^_]+)__/);
    if (undM) { out.push(<u key={`k${key++}`}>{renderInline(undM[1])}</u>); i += undM[0].length; continue; }
    // Italic *text*
    const italM = rest.match(/^\*([^*]+)\*/);
    if (italM) { out.push(<em key={`k${key++}`}>{renderInline(italM[1])}</em>); i += italM[0].length; continue; }
    // Code `text`
    const codeM = rest.match(/^`([^`]+)`/);
    if (codeM) { out.push(<code key={`k${key++}`} className="px-1 rounded bg-slate-100 text-[0.9em]">{codeM[1]}</code>); i += codeM[0].length; continue; }
    // Plain char — consume until the next special char
    const nextSpecial = rest.slice(1).search(/[*_`[]/);
    const chunkLen = nextSpecial === -1 ? rest.length : nextSpecial + 1;
    pushText(rest.slice(0, chunkLen));
    i += chunkLen;
  }
  return out;
}

/** Strip markdown for search/preview contexts. */
export function stripMarkdown(text) {
  if (!text) return '';
  return String(text)
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^[-*]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '');
}
